import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { useSessionStorage } from "usehooks-ts";
import { v4 as uuidv4 } from "uuid";

import {
  isRetriableInvokeError,
  UNIFIED_COPILOT_AGENT_ID,
  UNIFIED_COPILOT_TERMINAL_STATUSES,
  unifiedCopilotSessionKeys,
  useUnifiedCopilotCancel,
  useUnifiedCopilotInvoke,
  useUnifiedCopilotMessages,
} from "src/api/agents/unifiedCopilot";
import {
  type CopilotInvokeRequest,
  type CopilotInvokeRequestContentBlocksInner,
  type SessionMessagesResponseMessagesInner,
  type ToolMessage,
  ToolType,
} from "src/clients/agent-management";
import { useIsCopilotPrewarming } from "src/copilot/useCopilotPrewarm";
import { withClientAdvertisement } from "src/unifiedCopilot/chat/clientAdvertisement";
import {
  useClientContextData,
  withClientContextData,
} from "src/unifiedCopilot/chat/clientContextData";
import { clientMeta } from "src/unifiedCopilot/chat/clientMeta";
import {
  type ChatAttachmentPayload,
  mapAttachmentsToCopilotFileBlocks,
  mapPromptContentToCopilotContentBlocks,
} from "src/unifiedCopilot/chat/contentBlocks";
import {
  isSessionBusy,
  isSessionInterruptible,
  type QueuedDraft,
  type UnifiedCopilotChatSession,
  UnifiedCopilotChatSessionContext,
  type UnifiedCopilotSessionState,
} from "src/unifiedCopilot/chat/UnifiedCopilotChatSessionContext";
import {
  useAutomaticallyAddedContextualEntities,
  useCopilotNotifyMCPMutation,
} from "src/unifiedCopilot/context/internalHooks";
import {
  type ContextualEntityKey,
  contextualEntityKey,
} from "src/unifiedCopilot/context/types";
import { mapContextualEntitiesToCopilotContext } from "src/unifiedCopilot/context/utils";
import { getLocalTimezone } from "src/utils/timezones";

const UNIFIED_COPILOT_SELECTED_SESSION_STORAGE_KEY_PREFIX =
  "unifiedCopilotSelectedSessionV1";

const AWAITING_SESSION_TIMEOUT_MS = 60_000;

const FLUSH_RETRY_LIMIT = 30;
const FLUSH_RETRY_DELAY_MS = 1_000;

type SelectedSessionStorageKeyParams = {
  userId: string;
  workspaceId: string;
};

export const getUnifiedCopilotSelectedSessionStorageKey = ({
  userId,
  workspaceId,
}: SelectedSessionStorageKeyParams): string =>
  `${UNIFIED_COPILOT_SELECTED_SESSION_STORAGE_KEY_PREFIX}:${userId}:${workspaceId}`;

type SessionActivationMode = "new" | "resume";

type CreateUnifiedCopilotChatSessionParams = {
  userId: string;
  workspaceId: string;
};

type UnifiedCopilotChatSessionInternals = UnifiedCopilotChatSession & {
  /**
   * Session id held for the conversation after this one, reserved as soon as
   * the current session is invoked so its container can be warmed before the
   * user resets. `null` while the current session is still unused.
   */
  nextSessionId: string | null;
};

const mutatingMCPToolCallIds = (
  messages: SessionMessagesResponseMessagesInner[],
): string[] =>
  messages
    .filter(
      (message): message is { type: "tool" } & ToolMessage =>
        message.type === "tool" &&
        message.metadata.type === ToolType.MCP &&
        message.metadata.annotations?.read_only_hint !== true,
    )
    .map((message) => message.tool_call_id);

const deriveSessionState = ({
  isLoadingSession,
  isRunningUntouched,
  isBooting,
  hasArmedQueue,
  isActive,
}: {
  isLoadingSession: boolean;
  isRunningUntouched: boolean;
  isBooting: boolean;
  hasArmedQueue: boolean;
  isActive: boolean;
}): UnifiedCopilotSessionState => {
  if (isLoadingSession) return "restoring";
  // Outranks `queued`: a queue armed while the run is NOT stopping means a
  // cancel failed and handed the run back, so Stop must cancel again rather
  // than retract the queue.
  if (isRunningUntouched) return isBooting ? "booting" : "running";
  if (hasArmedQueue) return "queued";
  if (isActive) return "stopping";
  return "idle";
};

// Joins drafts into one invocation: every draft keeps its own text blocks
// (drafts separated by a blank line) and contributes its file blocks.
const buildDraftContentBlocks = (
  drafts: QueuedDraft[],
): CopilotInvokeRequestContentBlocksInner[] => {
  const textBlocks = drafts
    .map((draft) => mapPromptContentToCopilotContentBlocks(draft.markdown))
    .filter((blocks) => blocks.length > 0)
    .flatMap((blocks, index) =>
      index === 0
        ? blocks
        : [{ type: "text" as const, text: "\n\n" }, ...blocks],
    );
  const fileBlocks = mapAttachmentsToCopilotFileBlocks(
    drafts.flatMap((draft) => draft.attachments),
  );
  return [...textBlocks, ...fileBlocks];
};

export const useUnifiedCopilotChatSessionState = ({
  userId,
  workspaceId,
}: CreateUnifiedCopilotChatSessionParams): UnifiedCopilotChatSessionInternals => {
  const queryClient = useQueryClient();
  const automaticContextualEntities = useAutomaticallyAddedContextualEntities();
  const notifyMCPMutation = useCopilotNotifyMCPMutation();
  const clientContextData = useClientContextData();
  // Keys the user removed from this conversation's automatic context.
  const [dismissedContextualEntityKeys, setDismissedContextualEntityKeys] =
    useState<ReadonlySet<ContextualEntityKey>>(() => new Set());
  const contextualEntities = automaticContextualEntities.filter(
    (entity) => !dismissedContextualEntityKeys.has(contextualEntityKey(entity)),
  );

  const selectedSessionStorageKey = getUnifiedCopilotSelectedSessionStorageKey({
    userId,
    workspaceId,
  });

  // `useSessionStorage` re-reads its initial value in a mount effect, so the
  // value has to be stable: a generator mints a second id there and moves the
  // session off the one the pre-warm ping already warmed. The provider
  // remounts per `{userId, workspaceId}`, so one id per mount is the right
  // scope for the fallback.
  const [fallbackSessionId] = useState(() => uuidv4());

  const [sessionId, setSessionId] = useSessionStorage<string>(
    selectedSessionStorageKey,
    fallbackSessionId,
  );
  const isPrewarming = useIsCopilotPrewarming(
    UNIFIED_COPILOT_AGENT_ID,
    sessionId,
  );

  const messagesQueryKey = unifiedCopilotSessionKeys.messages(
    workspaceId,
    sessionId,
  );
  // Mirrors sessionId for callbacks of invocations that resolve after the
  // session they belong to was replaced by resetSession.
  const currentSessionIdRef = useRef(sessionId);
  useEffect(() => {
    currentSessionIdRef.current = sessionId;
  }, [sessionId]);
  const awaitingSessionRef = useRef(false);
  const awaitingTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [isAwaitingSession, setIsAwaitingSession] = useState(false);
  const [activationMode, setActivationMode] =
    useState<SessionActivationMode>("new");
  const [nextSessionId, setNextSessionId] = useState<string | null>(null);
  const [stopRequested, setStopRequested] = useState(false);
  const [queuedDrafts, setQueuedDrafts] = useState<QueuedDraft[]>([]);
  const [isQueueArmed, setIsQueueArmed] = useState(false);
  const stopIntentRef = useRef(false);
  const flushGuardRef = useRef(false);

  const clearAwaitingSession = useCallback((): void => {
    awaitingSessionRef.current = false;
    setIsAwaitingSession(false);
    clearTimeout(awaitingTimeoutRef.current);
  }, []);

  const beginAwaitingSession = useCallback((): void => {
    awaitingSessionRef.current = true;
    setIsAwaitingSession(true);
    clearTimeout(awaitingTimeoutRef.current);
    awaitingTimeoutRef.current = setTimeout(
      clearAwaitingSession,
      AWAITING_SESSION_TIMEOUT_MS,
    );
  }, [clearAwaitingSession]);

  const handleInvokeStarted = useCallback(
    (invokedSessionId: CopilotInvokeRequest["session_id"]): void => {
      // Only the current session may enter the awaiting state; reset() does
      // not stop an in-flight invoke, so its success can arrive afterwards.
      if (invokedSessionId === currentSessionIdRef.current) {
        beginAwaitingSession();
      }
    },
    [beginAwaitingSession],
  );

  const invokeMutation = useUnifiedCopilotInvoke({
    messagesQueryKey,
    onInvokeStarted: handleInvokeStarted,
  });
  const flushMutation = useUnifiedCopilotInvoke({
    messagesQueryKey,
    onInvokeStarted: handleInvokeStarted,
    retry: (failureCount, error) =>
      failureCount < FLUSH_RETRY_LIMIT && isRetriableInvokeError(error),
    retryDelay: FLUSH_RETRY_DELAY_MS,
  });
  const cancelMutation = useUnifiedCopilotCancel({ messagesQueryKey });

  useEffect(
    () => () => {
      clearTimeout(awaitingTimeoutRef.current);
    },
    [],
  );

  const messagesQuery = useUnifiedCopilotMessages(sessionId, {
    awaitingSessionRef,
  });
  const isLoadingSession =
    activationMode === "resume" && messagesQuery.isLoading;
  const sessionStatus = messagesQuery.session?.status;
  const observedMCPToolCallsRef = useRef<{
    sessionId: string | null;
    ids: Set<string>;
  }>({ sessionId, ids: new Set() });

  useEffect(() => {
    // The session changed, so we need to reset the observed tool calls
    const observed =
      observedMCPToolCallsRef.current.sessionId === sessionId
        ? observedMCPToolCallsRef.current.ids
        : new Set<string>();
    // Is there any mutating tool call we haven't observed yet?
    const newlyObservedIds = mutatingMCPToolCallIds(
      messagesQuery.messages,
    ).filter((toolCallId) => !observed.has(toolCallId));

    //Update the observed tool calls
    observedMCPToolCallsRef.current = {
      sessionId,
      ids: new Set([...observed, ...newlyObservedIds]),
    };

    // If there are any new mutating tool calls, we notify the context
    // so we trigger all the refetches
    if (newlyObservedIds.length > 0) {
      notifyMCPMutation();
    }
  }, [messagesQuery.messages, notifyMCPMutation, sessionId]);

  useEffect(() => {
    if (
      sessionStatus !== undefined &&
      !invokeMutation.isPending &&
      isAwaitingSession
    ) {
      clearAwaitingSession();
    }
  }, [
    clearAwaitingSession,
    invokeMutation.isPending,
    isAwaitingSession,
    sessionStatus,
  ]);

  const isBackendActive =
    sessionStatus !== undefined &&
    !UNIFIED_COPILOT_TERMINAL_STATUSES.has(sessionStatus);
  const isActive =
    invokeMutation.isPending ||
    flushMutation.isPending ||
    isAwaitingSession ||
    isBackendActive;
  // The agent is working and no stop is in effect — the run is ours to cancel.
  const isRunningUntouched = isActive && !stopRequested;
  // A queued turn is set to go out on its own at the terminal status.
  const hasArmedQueue = isQueueArmed && queuedDrafts.length > 0;
  // The stopped run ended with nothing armed to flush — back to plain idle.
  if (stopRequested && !isActive && !hasArmedQueue) {
    setStopRequested(false);
  }
  const sessionState = deriveSessionState({
    isLoadingSession,
    isRunningUntouched,
    isBooting:
      isPrewarming &&
      !isBackendActive &&
      (invokeMutation.isPending || flushMutation.isPending),
    hasArmedQueue,
    isActive,
  });
  // Only a settled-empty read proves the session is unused. A transcript still
  // loading — a session restored from storage reads as `idle` while it does —
  // or one whose read failed reports no messages either, and there the
  // conversation the user wants to leave may well exist.
  const isUnusedSession =
    sessionState === "idle" &&
    messagesQuery.messages.length === 0 &&
    queuedDrafts.length === 0 &&
    !messagesQuery.isLoading &&
    !messagesQuery.isError;

  const requestCancel = (): void => {
    cancelMutation.mutate(
      { sessionId },
      // Failed cancel: the run is still going — revert the optimistic stop.
      { onError: () => setStopRequested(false) },
    );
  };

  const submitDrafts = (
    drafts: QueuedDraft[],
    { includesQueued }: { includesQueued: boolean },
  ): void => {
    const contentBlocks = buildDraftContentBlocks(drafts);
    if (contentBlocks.length === 0) {
      return;
    }

    const request: CopilotInvokeRequest = {
      agent_id: UNIFIED_COPILOT_AGENT_ID,
      session_id: sessionId,
      content_blocks: contentBlocks,
      context: withClientContextData(
        withClientAdvertisement(
          mapContextualEntitiesToCopilotContext(contextualEntities),
        ),
        clientContextData,
      ),
      meta: clientMeta(),
      client_timezone: getLocalTimezone(),
    };

    // useSessionStorage does not write its initial value until it is set.
    setSessionId(sessionId);
    cancelMutation.reset();
    const mutation = includesQueued ? flushMutation : invokeMutation;
    mutation.mutate(request, {
      onSuccess: () => {
        if (stopIntentRef.current) {
          // Stop was clicked while this invoke was in flight; the session
          // row exists now, so the cancel can land.
          stopIntentRef.current = false;
          requestCancel();
        }
      },
      onError: () => {
        clearAwaitingSession();
        stopIntentRef.current = false;
        if (includesQueued) {
          // Hold the drafts in the trace, ahead of anything queued while this
          // invoke was in flight; the next submission retries them in order.
          setQueuedDrafts((current) => [...drafts, ...current]);
          setIsQueueArmed(false);
        }
      },
    });

    // The session in use is no longer a candidate for a second, parallel
    // conversation, so reserve the id the next one will run under and let
    // the provider warm its container ahead of the reset.
    setNextSessionId((reserved) => reserved ?? uuidv4());
  };

  const send = (
    message: string,
    attachments: ChatAttachmentPayload[] = [],
  ): void => {
    if (
      (message.trim().length === 0 && attachments.length === 0) ||
      isSessionBusy(sessionState)
    ) {
      return;
    }

    const draft: QueuedDraft = {
      id: uuidv4(),
      markdown: message,
      attachments,
    };

    // Raw `isActive`, not the state: "queued" folds together the unwinding
    // run and the frame after it goes terminal, and only the former queues.
    if (isActive) {
      // Unwinding after a stop: hold the draft; it flushes at the terminal
      // status (or rides along with a later submission if disarmed).
      flushGuardRef.current = false;
      setQueuedDrafts((previous) => [...previous, draft]);
      setIsQueueArmed(true);
      return;
    }

    const heldDrafts = queuedDrafts;
    if (heldDrafts.length > 0) {
      setQueuedDrafts([]);
      setIsQueueArmed(false);
    }
    submitDrafts([...heldDrafts, draft], {
      includesQueued: heldDrafts.length > 0,
    });
  };

  const stop = (): void => {
    // "queued" already implies a stop is in effect, so Stop retracts the
    // turn. It is the rule order in deriveSessionState that keeps a failed
    // cancel out of this branch — that reads "running" again, so Stop below
    // retries the cancel instead of silently disarming.
    if (sessionState === "queued") {
      // Hold the queued turn; it goes out with the next submission.
      setIsQueueArmed(false);
      return;
    }
    if (!isSessionInterruptible(sessionState)) {
      return;
    }

    setStopRequested(true);
    if (invokeMutation.isPending || flushMutation.isPending) {
      // The session row may not exist until the invoke resolves; the cancel
      // fires from that invoke's onSuccess instead.
      stopIntentRef.current = true;
      return;
    }
    requestCancel();
  };

  // Reacts to the polled terminal transition: once the stopped run is over,
  // the armed queue flushes as one combined invocation. Runs every render
  // (cheap guards); flushGuardRef keeps StrictMode's double-invoked effects
  // from firing the invoke twice.
  // Raw `isActive` again: "queued" covers the unwinding run too, and flushing
  // there would reopen the session before the cancel has landed.
  const shouldFlush = hasArmedQueue && !isActive;
  useEffect(() => {
    if (!shouldFlush || flushGuardRef.current) {
      return;
    }
    flushGuardRef.current = true;
    const drafts = queuedDrafts;
    setQueuedDrafts([]);
    setIsQueueArmed(false);
    setStopRequested(false);
    submitDrafts(drafts, { includesQueued: true });
  });

  const dismissContextualEntity = (key: ContextualEntityKey): void => {
    setDismissedContextualEntityKeys((previous) => new Set([...previous, key]));
  };

  const restoreContextualEntities = useCallback((): void => {
    setDismissedContextualEntityKeys(new Set());
  }, []);

  const activateSession = useCallback(
    (targetSessionId: string, mode: SessionActivationMode): void => {
      if (targetSessionId === sessionId) {
        return;
      }

      // Synchronously, so an in-flight invoke resolving before the re-render
      // already sees the replacement session.
      currentSessionIdRef.current = targetSessionId;
      clearAwaitingSession();
      invokeMutation.reset();
      flushMutation.reset();
      cancelMutation.reset();
      // Stop/queue state belongs to the session it was created in.
      stopIntentRef.current = false;
      flushGuardRef.current = false;
      setStopRequested(false);
      setQueuedDrafts([]);
      setIsQueueArmed(false);
      // Context dismissals belong to the session they were made in.
      setDismissedContextualEntityKeys(new Set());
      setActivationMode(mode);
      if (mode === "new") {
        queryClient.removeQueries({
          queryKey: messagesQueryKey,
          exact: true,
        });
      }
      setSessionId(targetSessionId);
    },
    [
      cancelMutation.reset,
      clearAwaitingSession,
      flushMutation.reset,
      invokeMutation.reset,
      messagesQueryKey,
      queryClient,
      setSessionId,
      sessionId,
    ],
  );

  const resetSession = useCallback((): void => {
    // Prefer the reserved id: its container was warmed when the outgoing
    // session was first invoked, so the new conversation starts warm.
    activateSession(nextSessionId ?? uuidv4(), "new");
    setNextSessionId(null);
  }, [activateSession, nextSessionId]);

  const resumeSession = useCallback(
    (targetSessionId: string): void => {
      if (targetSessionId.length === 0) return;
      activateSession(targetSessionId, "resume");
    },
    [activateSession],
  );

  return {
    messages: messagesQuery.messages,
    hasNextPage: messagesQuery.hasNextPage,
    isFetchingNextPage: messagesQuery.isFetchingNextPage,
    fetchNextPage: messagesQuery.fetchNextPage,
    sessionState,
    isUnusedSession,
    queuedDrafts,
    contextualEntities,
    sessionId,
    nextSessionId,
    liveStatusDetail: messagesQuery.session?.live_status_detail ?? null,
    send,
    stop,
    dismissContextualEntity,
    restoreContextualEntities,
    resetSession,
    resumeSession,
  };
};

export const useUnifiedCopilotChatSession = (): UnifiedCopilotChatSession => {
  const session = useContext(UnifiedCopilotChatSessionContext);
  if (!session) {
    throw new Error("UnifiedCopilotChatSessionContext is not defined");
  }
  return session;
};
